import { useState, useEffect } from "react";
import { Activity, AlertCircle } from "lucide-react";
import axios from "axios";
import StatisticsTable from "@/components/StatisticsTable";
import PidTable from "@/components/PidTable";
import StatsCards from "@/components/StatsCards";
import Header from "@/components/Header";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster } from "@/components/ui/sonner";

export const Dashboard = () => {
  const [telemetryData, setTelemetryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchTelemetryData = async () => {
    try {
      setError(null);
      const response = await axios.get("https://coopsys.app/metrics/pg");
      setTelemetryData(response.data.metric);
      setLastUpdate(new Date());
      setLoading(false);
    } catch (err) {
      console.error("Error fetching telemetry data:", err);
      setError("Failed to fetch telemetry data. Using mock data for demonstration.");
      // Mock data for demonstration
      setTelemetryData({
        estadistica: {
          service: "pm2-pg-metric",
          timestamp: new Date().toISOString(),
          uptime: 0.218110733,
          data: [ ]
        },
        pid: [ ]
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTelemetryData();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchTelemetryData();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleRefresh = () => {
    setLoading(true);
    fetchTelemetryData();
  };

  if (loading && !telemetryData) {
    return (
      <div className="min-h-screen bg-background">
        <div className="border-b border-border bg-card">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Skeleton className="h-16 w-full" />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Toast notifications */}
      <Toaster richColors position="top-right" />
      
      {/* Header */}
      <Header lastUpdate={lastUpdate} loading={loading} onRefresh={handleRefresh} />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {error && (
          <Alert variant="destructive" className="bg-warning/10 border-warning text-warning-foreground">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Stats Cards */}
        {telemetryData && (
          <StatsCards
            estadistica={telemetryData.estadistica}
            pidData={telemetryData.pid}
          />
        )}

        {/* Statistics Table */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Database Statistics</h2>
          </div>
          {telemetryData && (
            <StatisticsTable 
              data={telemetryData.estadistica?.data || []} 
              onRefresh={handleRefresh}
            />
          )}
        </div>

        {/* PID Table */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <Activity className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Process Information (PID)</h2>
          </div>
          {telemetryData && (
            <PidTable 
              data={telemetryData.pid || []} 
              onProcessKilled={handleRefresh}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
